import React from 'react';
import './cardBrandMark.css';
import visaMark from './cardMarks/visa.svg';
import mastercardMark from './cardMarks/mastercard.svg';
import amexMark from './cardMarks/amex.svg';
import discoverMark from './cardMarks/discover.svg';
import dinersMark from './cardMarks/diners.svg';

/**
 * ONE source of card-scheme artwork for every client surface (2026-08-13):
 * the embedded checkout header, the saved-card picker and Account → Payment
 * methods all render the same marks, so a saved Visa looks like the Visa the
 * client just typed in. Artwork is bundled (no external requests) and shown in
 * a uniform tile — Amex/Discover ship as full card art and fill their tile.
 */

const MARKS = {
    VISA: { src: visaMark, fit: 'glyph', label: 'Visa' },
    MASTERCARD: { src: mastercardMark, fit: 'glyph', label: 'Mastercard' },
    MASTER: { src: mastercardMark, fit: 'glyph', label: 'Mastercard' },
    MC: { src: mastercardMark, fit: 'glyph', label: 'Mastercard' },
    AMERICAN_EXPRESS: { src: amexMark, fit: 'art', label: 'American Express' },
    AMEX: { src: amexMark, fit: 'art', label: 'American Express' },
    DISCOVER: { src: discoverMark, fit: 'art', label: 'Discover' },
    DINERS_CLUB_INTERNATIONAL: { src: dinersMark, fit: 'glyph', label: 'Diners Club' },
    DINERS: { src: dinersMark, fit: 'glyph', label: 'Diners Club' },
    DINERS_CLUB: { src: dinersMark, fit: 'glyph', label: 'Diners Club' },
};

/** The scheme string the gateway stores varies in case/spacing — normalise. */
export function markFor(scheme) {
    const key = String(scheme || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
    return MARKS[key] || null;
}

/** A single scheme's mark. Unknown schemes fall back to a neutral card tile. */
export default function CardBrandMark({ scheme, className = '' }) {
    const mark = markFor(scheme);
    if (!mark) {
        return (
            <span className={`cbm cbm--empty ${className}`} aria-label={scheme || 'Card'}>
                <span className="cbm__stripe" aria-hidden="true" />
            </span>
        );
    }
    return (
        <span className={`cbm ${mark.fit === 'art' ? 'cbm--art' : ''} ${className}`}>
            <img
                className={mark.fit === 'art' ? 'cbm__img cbm__img--art' : 'cbm__img cbm__img--glyph'}
                src={mark.src}
                alt={mark.label}
            />
        </span>
    );
}

/** The row of everything this outlet accepts — used by the checkout header. */
export const ACCEPTED_SCHEMES = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', 'DINERS'];

export function AcceptedCardMarks({ className = '' }) {
    return (
        <span className={`cbm-row ${className}`} aria-label="Visa, Mastercard, American Express, Discover and Diners Club accepted">
            {ACCEPTED_SCHEMES.map((s) => <CardBrandMark key={s} scheme={s} />)}
        </span>
    );
}

/**
 * How a saved card is named everywhere (2026-08-13): the client's own
 * nickname when they gave one, otherwise the scheme — then the last four
 * digits. Never the full masked PAN: "•••• 1111" is what people recognise.
 */
export function cardLast4(maskedPan) {
    const digits = String(maskedPan || '').replace(/\D/g, '');
    return digits ? digits.slice(-4) : '';
}

export function cardLabel(card) {
    const last4 = cardLast4(card?.masked_pan);
    const name = (card?.nickname || '').trim() || markFor(card?.scheme)?.label || card?.scheme || 'Card';
    return last4 ? `${name} •••• ${last4}` : name;
}
